import React, { useEffect, useRef } from 'react';

const GRID_SPACING = 24;
const MOUSE_RADIUS = 280;
const BG_COLOR = '#0a0a0f';

const GridRevealBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.active = true;
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      const mouse = mouseRef.current;

      ctx.fillStyle = BG_COLOR;
      ctx.fillRect(0, 0, w, h);

      const startX = Math.floor(-w / 2 / GRID_SPACING) * GRID_SPACING + (w / 2) % GRID_SPACING;
      const startY = Math.floor(-h / 2 / GRID_SPACING) * GRID_SPACING + (h / 2) % GRID_SPACING;
      const endX = w + GRID_SPACING * 2;
      const endY = h + GRID_SPACING * 2;

      ctx.lineWidth = 1;

      for (let x = startX; x <= endX; x += GRID_SPACING) {
        const dist = mouse.active ? Math.abs(x - mouse.x) : 9999;
        const alpha = dist < MOUSE_RADIUS
          ? 0.04 + (1 - dist / MOUSE_RADIUS) * 0.14
          : 0.02;
        ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = startY; y <= endY; y += GRID_SPACING) {
        const dist = mouse.active ? Math.abs(y - mouse.y) : 9999;
        const alpha = dist < MOUSE_RADIUS
          ? 0.04 + (1 - dist / MOUSE_RADIUS) * 0.14
          : 0.02;
        ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      requestAnimationFrame(draw);
    };
    const raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0"
      style={{ width: '100vw', height: '100vh' }}
      aria-hidden="true"
    />
  );
};

export default GridRevealBackground;
